import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

import SiteLayout from "./pages/website/SiteLayout";
import Home from "./pages/website/Home";
import ServicePage from "./pages/website/ServicePage";
import Team from "./pages/website/Team";
import Careers from "./pages/website/Careers";
import Publications from "./pages/website/Publications";
import { ToolsIndex, ToolRun } from "./pages/website/PublicTools";
import Gallery from "./pages/website/Gallery";
import WebsiteAdmin from "./pages/portal/WebsiteAdmin";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import PortalLayout from "./pages/portal/PortalLayout";
import Dashboard from "./pages/portal/Dashboard";
import Clients from "./pages/portal/Clients";
import Projects from "./pages/portal/Projects";
import ProjectDetail from "./pages/portal/ProjectDetail";
import Timesheet from "./pages/portal/Timesheet";
import Reimbursements from "./pages/portal/Reimbursements";
import Approvals from "./pages/portal/Approvals";
import People from "./pages/portal/People";
import Leave from "./pages/portal/hr/LeaveCenter";
import HrApprovals from "./pages/portal/hr/HrApprovals";
import HrDesk from "./pages/portal/hr/HrDesk";
import Holidays from "./pages/portal/Holidays";
import Policies from "./pages/portal/Policies";
import Payroll from "./pages/portal/Payroll";
import Reports from "./pages/portal/Reports";
import ClientDetail from "./pages/portal/ClientDetail";
import DataManagement from "./pages/portal/DataManagement";
import StatusModule from "./pages/portal/Status";
import CompOff from "./pages/portal/hr/CompOffCenter";
import Billing from "./pages/portal/Billing";
import MIS from "./pages/portal/MIS";
import Backup from "./pages/portal/Backup";
import Tasks from "./pages/portal/Tasks";
import KipUpdates from "./pages/portal/KipUpdates";
import KipDocuments from "./pages/portal/KipDocuments";
import KipMarkets from "./pages/portal/KipMarkets";
import KipFinancialCentre from "./pages/portal/KipFinancialCentre";
import KipAssistant from "./pages/portal/KipAssistant";
import KipBulletin from "./pages/portal/KipBulletin";
import Tools from "./pages/portal/Tools";
import KipDashboard from "./pages/portal/KipDashboard";
import AccessManagement from "./pages/portal/AccessManagement";
import FinanceIO from "./pages/portal/FinanceIO";
import HolidaysPolicies from "./pages/portal/HolidaysPolicies";
import FixedAssets from "./pages/portal/FixedAssets";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-sm uppercase tracking-[0.3em] text-slate-500">Loading</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronously detect Emergent OAuth session_id in URL fragment before anything else
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/expertise" element={<Navigate to="/#expertise" replace />} />
        <Route path="/expertise/:slug" element={<ServicePage />} />
        <Route path="/team" element={<Team />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/lets-learn" element={<Publications />} />
        <Route path="/publications" element={<Navigate to="/lets-learn" replace />} />
        <Route path="/tools" element={<ToolsIndex />} />
        <Route path="/tools/:toolId" element={<ToolRun />} />
        <Route path="/life-at-vcaj" element={<Gallery />} />
        <Route path="/gallery" element={<Navigate to="/life-at-vcaj" replace />} />
      </Route>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/portal"
        element={
          <ProtectedRoute>
            <PortalLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="clients" element={<Clients />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:id" element={<ProjectDetail />} />
        <Route path="timesheet" element={<Timesheet />} />
        <Route path="reimbursements" element={<Reimbursements />} />
        <Route path="leave" element={<Leave />} />
        <Route path="holidays" element={<HolidaysPolicies />} />
        <Route path="policies" element={<HolidaysPolicies />} />
        <Route path="approvals" element={<Approvals />} />
        <Route path="people" element={<People />} />
        <Route path="payroll" element={<Payroll />} />
        <Route path="reports" element={<Reports />} />
        <Route path="status" element={<StatusModule />} />
        <Route path="comp-off" element={<CompOff />} />
        <Route path="hr-approvals" element={<HrApprovals />} />
        <Route path="hr" element={<HrDesk />} />
        <Route path="billing" element={<Billing />} />
        <Route path="mis" element={<MIS />} />
        <Route path="backup" element={<Backup />} />
        <Route path="data-management" element={<DataManagement />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="access" element={<AccessManagement />} />
        <Route path="finance-io" element={<FinanceIO />} />
        <Route path="laptop-tracker" element={<FixedAssets />} />
        <Route path="website" element={<WebsiteAdmin key="website" />} />
        <Route path="gallery" element={<WebsiteAdmin key="gallery" initialTab="gallery" />} />
        <Route path="fixed-assets" element={<FixedAssets />} />
        <Route path="kip" element={<KipDashboard />} />
        <Route path="kip/updates" element={<KipUpdates />} />
        <Route path="kip/documents" element={<KipDocuments />} />
        <Route path="kip/markets" element={<KipMarkets />} />
        <Route path="kip/centre" element={<KipFinancialCentre />} />
        <Route path="kip/assistant" element={<KipAssistant />} />
        <Route path="kip/bulletin" element={<KipBulletin />} />
        <Route path="tools" element={<Tools />} />
        <Route path="tools/:toolId" element={<Tools />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
